/**
 * S21. The bundle, and whether this app is running an old one.
 *
 * Every app is built on top of a bundle that SV Cloud keeps improving. An app
 * that never catches up keeps working, but it stops being able to do things
 * the platform can now do, and it can keep a build step that fails for reasons
 * nothing in its own code explains. This screen is where that gets said.
 *
 * NOBODY READING THIS SCREEN IS GOING TO EDIT THE FILES THEMSELVES, and the
 * screen is built on that fact rather than around it. The one action is a
 * sentence you hand to your coding agent, sitting in a copy field, because the
 * agent is the thing that can read the same plan through the platform's tools
 * and do the merging safely. A list of file paths with an Apply button would
 * be a promise this product cannot keep: the platform will not write to
 * anybody's repository, and it should not.
 *
 * UP TO DATE IS A STATEMENT, NOT A SHRUG. Same doctrine as the no errors state
 * on activity: a calm line, the version, and no button, because a button would
 * imply there is something to do.
 *
 * THE OLD BUILD STEP WARNING IS SIMPLE AND SITS ABOVE EVERYTHING. It is the
 * one thing on this screen that is already costing somebody something, and it
 * is never behind Advanced. It is also never framed as blocking: nothing about
 * an old bundle stops a deploy, and saying otherwise would be a screen
 * frightening a person about a state the platform is fine with.
 *
 * SIMPLE VERSUS ADVANCED. Simple gets the sentence, the copy field, and a
 * count of what will change. Advanced appends the file ledger with each file's
 * path and what happens to it. The counts and the action are identical in both
 * modes: Advanced never removes and never replaces.
 *
 * FILE PATHS ARE ADVANCED FOR A REASON THAT IS NOT SNOBBERY. In Simple, the
 * per file rows would be the biggest thing on the page and the least
 * actionable: a person who cannot act on src/storage.ts is worse off looking
 * at it, not better informed.
 *
 * FAKEABLE STATES, deep linkable per prototype mechanic (a):
 *   ?state=behind    an update available, with a mixed plan
 *   ?state=current   up to date
 *   ?state=legacy    no recorded starting point, plus the old build step
 *   ?state=loading   the skeletons, held open
 */

import { useEffect } from 'react';
import { Link } from 'react-router';

import { AdvancedDisclosure } from '../../components/patterns/AdvancedDisclosure';
import { CopyField } from '../../components/patterns/CopyField';
import { Notice } from '../../components/patterns/Notice';
import { SectionPanel } from '../../components/patterns/SectionPanel';
import { Skeleton } from '../../components/patterns/Skeleton';

import { getProject } from '../../lib/api';
import { getBundlePlan, type BundleAction, type BundlePlan } from '../../lib/real-api';
import { captureEvent } from '../../lib/analytics/posthog';
import { useAdvanced } from '../../lib/hooks/use-advanced';
import { useAsync } from '../../lib/hooks/use-async';
import { useProjectSlug } from '../../lib/hooks/use-project-slug';
import { useScreenState } from '../../lib/hooks/use-screen-state';
import { t } from '../../lib/i18n/t';

import '../../styles/components/bundle.css';

const USE_MOCK = import.meta.env.VITE_API_MODE === 'mock';

const STATES = ['resting', 'behind', 'current', 'legacy', 'loading'] as const;
type StateName = (typeof STATES)[number];

/**
 * What each action means to the person, not to the merge engine. The wire's
 * own vocabulary is for an agent; this is the same fact said to an owner.
 *
 * `report-only` and `skip` never reach a row: the first is a file the update
 * does not touch, the second is one that did not change, and neither is worth
 * a line on a screen somebody opened to find out what is about to happen.
 */
const ACTION_COPY: Record<BundleAction, { label: string; note: string } | null> = {
  skip: null,
  'report-only': null,
  'fast-forward': {
    label: 'Replaced',
    note: 'You never changed this one, so it is swapped for the new version.',
  },
  conflict: {
    label: 'Merged',
    note: 'You changed this one and so did we. Your agent keeps both.',
  },
  create: { label: 'Added', note: 'New in this version of the bundle.' },
  'merge-manifest': {
    label: 'Merged',
    note: 'Your list of packages and commands gains what is missing. Nothing is removed.',
  },
  locate: {
    label: 'Needs a look',
    note: 'We expected this somewhere and it is not there. Your agent finds it first.',
  },
  retire: { label: 'Removed', note: 'No longer part of the bundle, and unused here.' },
  'retire-conflict': {
    label: 'Needs a decision',
    note: 'No longer part of the bundle, and your app uses it. Your agent will explain the options before changing anything.',
  },
};

/** The one action: a sentence somebody pastes into their coding agent. */
function agentPrompt(): string {
  return t(
    'Update this app to the newest SV Cloud bundle. Use the bundle_status and bundle_plan tools to get the plan, follow the action on each file, and update contents SVAGENTS.md with the latest bundle, do not commit or push. Tell me what changed before I commit.',
  );
}

interface Fixture extends BundlePlan {}

/** Design review only. Never reachable in a real build (useScreenState). */
function fixture(state: StateName): Fixture {
  const base: Fixture = {
    projectId: 'demo',
    currentVersion: '2026.12.9',
    latestVersion: '2026.12.13',
    updateAvailable: true,
    legacy: false,
    baseUnavailableReason: null,
    workflowStale: false,
    workflowWarning: null,
    framework: 'hono',
    migrateOnce: false,
    migrationNotes: null,
    instructions: '',
    files: [
      {
        src: 'worker/storage.ts',
        path: 'src/storage.ts',
        class: 'platform-shim',
        action: 'fast-forward',
        guidance: '',
      },
      {
        src: 'worker/index.ts',
        path: 'src/index.ts',
        class: 'user-code',
        action: 'report-only',
        guidance: '',
      },
      { src: 'AGENTS.md', path: 'SVAGENTS.md', class: 'platform-doc', action: 'fast-forward', guidance: '' },
      { src: 'package.json', path: 'package.json', class: 'manifest', action: 'merge-manifest', guidance: '' },
      {
        src: 'worker/logger.ts',
        path: 'src/logger.ts',
        class: 'platform-shim',
        action: 'conflict',
        guidance: '',
      },
    ],
  };
  if (state === 'current') {
    return { ...base, currentVersion: '2026.12.13', updateAvailable: false, files: [] };
  }
  if (state === 'legacy') {
    return {
      ...base,
      currentVersion: null,
      legacy: true,
      migrateOnce: true,
      workflowStale: true,
      workflowWarning:
        "This app's build step for design pictures was set up before we started asking for a file size, which storage now needs. That step fails until the bundle is updated.",
    };
  }
  return base;
}

export default function Bundle() {
  const slug = useProjectSlug();
  const [advanced] = useAdvanced();
  const { state } = useScreenState<StateName>(STATES, 'resting');

  const { data: project } = useAsync(
    () => (slug ? getProject(slug) : Promise.resolve(undefined)),
    [slug],
  );
  const projectId = project?.id ?? '';

  const faked = USE_MOCK && state !== 'resting' && state !== 'loading';

  const { data: live, isLoading } = useAsync(
    () =>
      faked || !projectId
        ? Promise.resolve(undefined)
        : getBundlePlan(projectId).catch(() => undefined),
    [faked, projectId],
  );

  const plan = faked ? fixture(state) : USE_MOCK ? fixture('behind') : live;

  useEffect(() => {
    if (!plan) return;
    captureEvent('bundle_screen_viewed', {
      project_slug: slug,
      current_version: plan.currentVersion,
      latest_version: plan.latestVersion,
      update_available: plan.updateAvailable,
      legacy: plan.legacy,
      workflow_stale: plan.workflowStale,
      changed_files: plan.files.filter((f) => ACTION_COPY[f.action] !== null).length,
    });
  }, [plan, slug]);

  // "Bundle" throughout, matching the CLI's `svcloud bundle`, the platform's
  // bundle_* tools and the route. The dashboard was the one surface still
  // calling it a starter, which is also a plan tier's name here.
  const title = t('Bundle version');

  if (state === 'loading' || (isLoading && !plan)) {
    return (
      <div className="page">
        <header className="page__head">
          <h1 className="page__title">{title}</h1>
        </header>
        <Skeleton shape="track" count={2} label={t('Checking your bundle')} />
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="page">
        <header className="page__head">
          <h1 className="page__title">{title}</h1>
        </header>
        <Notice tone="quiet" title={t('We could not check your bundle just now.')}>
          <p>{t('Nothing is wrong with your app. Try again in a moment.')}</p>
        </Notice>
      </div>
    );
  }

  const rows = plan.files.filter((f) => ACTION_COPY[f.action] !== null);
  const decisions = rows.filter(
    (f) => f.action === 'retire-conflict' || f.action === 'locate',
  ).length;

  return (
    <div className="page">
      <header className="page__head">
        <h1 className="page__title">{title}</h1>
        <p className="page__lede">
          {t('Every app is built on a bundle that we keep improving. This is where you catch up with it.')}
        </p>
      </header>

      {/* Above everything, and never behind Advanced: it is the one thing here
          already costing somebody something. */}
      {plan.workflowWarning ? (
        <Notice tone="attention" title={t('One of your build steps is failing.')}>
          <p>{plan.workflowWarning}</p>
        </Notice>
      ) : null}

      {!plan.updateAvailable ? (
        <SectionPanel title={t('Up to date')}>
          <div className="bundle__calm">
            <p>
              {t('This app is on the newest bundle, {version}.', {
                version: plan.latestVersion,
              })}
            </p>
            <p className="bundle__quiet">
              {t('We will say so here when there is a newer one.')}
            </p>
          </div>
        </SectionPanel>
      ) : (
        <>
          <SectionPanel
            title={t('A newer bundle is available')}
            note={
              plan.currentVersion
                ? t('You are on {current}. The newest is {latest}.', {
                    current: plan.currentVersion,
                    latest: plan.latestVersion,
                  })
                : t('The newest is {latest}.', { latest: plan.latestVersion })
            }
          >
            <div className="bundle__body">
              {plan.migrateOnce ? (
                <Notice tone="quiet" title={t('This app has no recorded starting point.')}>
                  <p>
                    {t('It was set up before we started recording which bundle an app is on, so this first update cannot tell which parts you changed. Your agent will treat every change as a suggestion and ask you about anything it is unsure of. After this, we will know.')}
                  </p>
                </Notice>
              ) : null}

              <p>
                {rows.length === 0
                  ? t('Nothing in your own code changes.')
                  : t('{n} files change. Your own code is not overwritten.', {
                      n: rows.length,
                    })}
              </p>

              {decisions > 0 ? (
                <p>
                  {t('{n} of them need a decision from you. Your agent will explain each one in plain language before changing anything.', { n: decisions })}
                </p>
              ) : null}

              <p className="bundle__quiet">
                {t('Open this app with your coding agent and give it this:')}
              </p>
              <CopyField
                label={t('Update request')}
                value={agentPrompt()}
                size="lg"
                onCopied={() =>
                  captureEvent('bundle_update_request_copied', {
                    project_slug: slug,
                    current_version: plan.currentVersion,
                    latest_version: plan.latestVersion,
                    changed_files: rows.length,
                    decisions,
                  })
                }
              />
              <p className="bundle__quiet">
                {t('It reads the same plan we do, and it will not commit anything without showing you first.')}
              </p>
            </div>
          </SectionPanel>

          {rows.length > 0 ? (
            <SectionPanel title={t('What changes')} flush>
              <AdvancedDisclosure label={t('Show me the files')} defaultOpen={advanced}>
                <ul className="bundle__files">
                  {rows.map((file) => {
                    const copy = ACTION_COPY[file.action];
                    if (!copy) return null;
                    return (
                      <li className="bundle__file" key={file.src}>
                        <span className="bundle__path mono">{file.path}</span>
                        <span className="bundle__action" data-action={file.action}>
                          {t(copy.label)}
                        </span>
                        <p className="bundle__note">{t(copy.note)}</p>
                        {file.retirement ? (
                          <p className="bundle__note">{file.retirement.note}</p>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </AdvancedDisclosure>
            </SectionPanel>
          ) : null}

          {plan.migrationNotes ? (
            <SectionPanel title={t('What is new')}>
              <pre className="bundle__notes">{plan.migrationNotes}</pre>
            </SectionPanel>
          ) : null}
        </>
      )}

      {slug ? (
        <p className="bundle__back">
          <Link to={`/p/${encodeURIComponent(slug)}/settings`}>{t('Back to settings')}</Link>
        </p>
      ) : null}
    </div>
  );
}
